import { Autocomplete, Button, Grid, Modal, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import * as React from 'react';
import { Controller, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../reduxState/store';
import { IMaskInput } from 'react-imask';
import { address, clearState } from '../../../reduxState/addressSlice/addressAddSlice';
import { fetchAddressById } from '../../../reduxState/addressSlice/addressGetbyIdSlice';
import { updateAddressById, clearUpdateState } from '../../../reduxState/addressSlice/addressUpdateSlice';
import modalCancel from '../../../assets/images/modalCancel.png'
import AppSnackbar from '../../commonToast/customizeAlert';

interface Props {
  isOpen: any;
  onClose: any;
  id : any;
}

const AddressType = [
  { value: 'Home', name: 'Home' },
  { value: 'Office', name: 'Office' },
  { value: 'Others', name: 'Others' },
];

const AddAddress: React.FC<Props> = ({ isOpen , onClose, id }) => {
  const dispatch = useDispatch<AppDispatch>();
  const addressAdd = useSelector((state: RootState) => state.addressAdd);
  const readAddress = useSelector((state: RootState) => state.addressById);
  const addressUpdate = useSelector((state: RootState) => state.addressUpdate);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = React.useState(false);

  React.useEffect(() => {
    if (addressAdd.error || addressUpdate.error) {
      setIsErrorSnackbarOpen(true);
    }
  }, [addressAdd.code, addressUpdate.code]);

  React.useEffect(() => {
    if(addressAdd.code === 200 || addressUpdate.code === 200) {
      onClose()
      reset({
        addressType: '',
        area: '',
        name: '',
        number: '',
        landMark: '',
        streetAddress: '',
        zipCode: ''
      })
    }
  }, [addressAdd.code && addressAdd.loading, addressUpdate.code && addressUpdate.loading])

  React.useEffect(() => {
    if (id) {
      dispatch(fetchAddressById(id));
    }
  }, [id]);

  const validationSchema = yup.object().shape({
    name: yup.string().required('Please enter the name'),
    number: yup.string().matches(/^[0-9]\d{9}$/, {message: "Please enter valid number.", excludeEmptyString: false}),
    streetAddress: yup.string().required('Please enter the street address'),
    zipCode: yup.string().required('').matches(/^(\d{6})$/, 'Please enter valid pin code'),
    area: yup.string().required('Please enter the area'),
    landMark: yup.string().required('Please enter the land mark'),
    addressType: yup.object().required('Please select the address type'),
  });

  const { handleSubmit, control, reset } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
        name:'',
        number: '',
        streetAddress:'',
        area:'',
        zipCode:'',
        landMark:'',
        addressType:{}
    }
  });

  const onSubmit = (event: any) => {
    const payload: any = {
        addressType: event.addressType.value.trim(),
        area: event.area.trim(),
        contactName: event.name.trim(),
        contactNo: event.number.trim(),
        landMark: event.landMark.trim(),
        streetAddress: event.streetAddress.trim(),
        zipCode: event.zipCode.trim(),
        lat: 13.025408357478305,
        longt: 80.1898450244078,
    }
    // const Geocode ='1600 Amphitheatre Parkway, Mountain View, CA'
    if (id) {
      // Address(Geocode)
      payload.id = id;
      dispatch((updateAddressById(payload)))
    } else {
      // Address(Geocode)
      dispatch(address(payload))
    }
  }

  React.useEffect(() => {
    const addressType = AddressType.find((item) => item.value === readAddress?.data?.addressType);
    reset({
      addressType: addressType,
      area: readAddress?.data?.area,
      name: readAddress?.data?.contactName,
      number: readAddress?.data?.contactNo,
      landMark: readAddress?.data?.landMark,
      streetAddress: readAddress?.data?.streetAddress,
      zipCode: readAddress?.data?.zipCode
    })
  }, [readAddress.data])

  const handleCloseButton = () => {
    onClose()
    reset()
  }

  const handleSnackbarClose = () => {
    setIsErrorSnackbarOpen(false);
    dispatch(clearUpdateState())
    dispatch(clearState())
  };

  return (
    <React.Fragment>
      <Modal open={isOpen} onClose={onClose}>
        <Box width={{xs:'80%', sm:'60%', md:'50%'}} sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, borderRadius: '30px' }}>
          <Box pt={3} pb={1} sx={{position:'relative'}}>
              <Typography className='address-add-head roboto-med' sx={{ textAlign: 'center' }}>{id ? "Edit Address" : "Add Address"}</Typography>
            <Button variant='text' className='close-address-button' onClick={handleCloseButton} sx={{position:'absolute', right:'20px', top:'20px'}}><img src={modalCancel} alt="Delete button" width={32} height={32} loading='lazy' /></Button>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)} style={{textAlign:'center', paddingLeft:'40px', paddingRight:'40px'}}>
            <Grid container justifyContent={'space-between'} columns={11}>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Name is required' }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            margin="normal"
                            className='add-address-input'
                            required
                            fullWidth
                            id="name"
                            label="Name"
                            name="name"
                            autoFocus
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            InputProps={{
                              onKeyPress: (event) => {
                                const regex = /^[a-zA-Z\s]+$/;
                                if (!regex.test(event.key) || value.length >= 20) {
                                  event.preventDefault();
                                }
                              },
                              autoComplete: 'off',
                              sx: {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        )}
                      />
                </Grid>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="number"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Mobile number is required' }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            margin="normal"
                            className='add-address-input'
                            required
                            fullWidth
                            id="number"
                            label="Mobile number"
                            name="number"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            InputProps={{
                                sx: {
                                  borderRadius: '8px'
                                },
                                autoComplete: 'off',
                            }}
                            inputProps={{
                              maxLength: 10,
                            }}
                          />
                        )}
                      />
                </Grid>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="streetAddress"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Street address is required' }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            margin="normal"
                            className='add-address-input'
                            required
                            fullWidth
                            id="streetAddress"
                            label="Street address/ Flat no"
                            name="streetAddress"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            InputProps={{
                              onKeyPress: (event) => {
                                const regex = /^[a-zA-Z0-9#@\s,]+$/;
                                if (!regex.test(event.key) || value.length >= 25) {
                                  event.preventDefault();
                                }
                              },
                              autoComplete: 'off',
                              sx: {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        )}
                      />
                </Grid>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="area"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Area is required' }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            margin="normal"
                            className='add-address-input'
                            required
                            fullWidth
                            id="area"
                            label="Area"
                            name="area"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            InputProps={{
                              onKeyPress: (event) => {
                                const regex = /^[a-zA-Z1-9#@\s,]+$/;
                                if (!regex.test(event.key) || value.length >= 25) {
                                  event.preventDefault();
                                }
                              },
                              autoComplete: 'off',
                              sx: {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        )}
                      />
                </Grid>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="landMark"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'LandMark is required' }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            margin="normal"
                            className='add-address-input'
                            required
                            fullWidth
                            id="landMark"
                            label="Landmark"
                            name="landMark"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            InputProps={{
                              onKeyPress: (event) => {
                                const regex = /^[a-zA-Z1-9#@\s,]+$/;
                                if (!regex.test(event.key) || value.length >= 25) {
                                  event.preventDefault();
                                }
                              },
                              autoComplete: 'off',
                              sx: {
                                borderRadius: '8px',
                              }
                            }}
                          />
                        )}
                      />
                </Grid>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="zipCode"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Zip Code is required' }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            margin="normal"
                            className='add-address-input'
                            required
                            fullWidth
                            id="zipCode"
                            label="Zip code"
                            name="zipCode"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                            InputProps={{
                                sx: {
                                  borderRadius: '8px'
                                },
                                autoComplete: 'off',
                                inputComponent: IMaskInput as any,
                                inputProps: {
                                  mask: '000000',
                                },
                            }}
                          />
                        )}
                      />
                </Grid>
                <Grid item xs={11} sm={5} md={5} lg={5}>
                    <Controller
                        name="addressType"
                        control={control}
                        defaultValue=""
                        rules={{ required: 'Address Type required' }}
                        render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                          <Autocomplete
                            sx={{ marginTop: '16px', marginBottom: '16px', borderRadius: '16px' }}
                            className='add-address-input add-address-autocomplete'
                            disablePortal
                            id="Address type"
                            value={value || ''}
                            options={AddressType}
                            getOptionLabel={(option: any) => option.name || ''}
                            onChange={(_, data: any) => { onChange(data) }}
                            renderInput={(params) => (<TextField inputRef={ref} className='address-type' {...params} inputProps={{
                              ...params.inputProps,
                              autoComplete: 'off',
                            }}
                              error={!!error} helperText={error ? error.message : null} label="Address type" />)}
                          />
                        )}
                      />
                </Grid>
            </Grid>
            <Button variant='contained' className='login-button reem-kulfi add-address' sx={{width: '20%', marginTop:'16px', marginBottom:'24px', padding:'8px'}} onClick={handleSubmit(onSubmit)}>{id ? "Update" : "Save"}</Button>
          </form>
          {isErrorSnackbarOpen && (
          <AppSnackbar
            showAlert={isErrorSnackbarOpen}
            message={addressAdd?.message || addressUpdate.message}
            type="error"
            onClose={handleSnackbarClose}
          />
        )}
        </Box>
      </Modal>
    </React.Fragment>
  );
};
export default AddAddress;